import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PoundSterling, RotateCcw, Save, Settings2, History } from "lucide-react";

// Task 1 — Owner-only editor for the Johnsons rate card. Mirrors the
// backend shape in routes/bedding.py:DEFAULT_RATES. Rows map to one
// numeric input each so Ops can tune a cell without touching the API.

const BED_SIZES = [
  { key: "double", label: "Double" },
  { key: "king", label: "King" },
  { key: "super_king", label: "Super king" },
  { key: "single", label: "Single" },
];

const SIMPLE_FIELDS = [
  { key: "pillowcase", label: "Pillowcase (ea.)" },
  { key: "hand_towel", label: "Hand towel" },
  { key: "face_cloth", label: "Face cloth" },
  { key: "bath_sheet", label: "Bath sheet" },
  { key: "bath_mat", label: "Bath mat" },
];

const _fmtGBP = (n) => `£${Number(n || 0).toFixed(3)}`;
const _fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch (_e) { return iso || ""; }
};

export default function BeddingRatesEditor({ open, onOpenChange, onSaved }) {
  const [rates, setRates] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bedding/rates");
      setRates(JSON.parse(JSON.stringify(data?.rates || {})));
      setDefaults(data?.default || null);
    } catch (err) {
      toast.error(apiError(err, "Load failed"));
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/bedding/rate-history");
      setHistory(data?.items || []);
    } catch (err) {
      toast.error(apiError(err, "History failed"));
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const setDuvetSheet = (field, size, value) => {
    setRates((prev) => ({
      ...prev,
      [field]: { ...(prev[field] || {}), [size]: Number(value) },
    }));
  };
  const setSimple = (field, value) => {
    setRates((prev) => ({ ...prev, [field]: Number(value) }));
  };

  const resetDefaults = () => {
    if (!defaults) return;
    if (!window.confirm("Reset rates to the September 2026 Johnsons card? Current edits will be lost (until you save).")) return;
    setRates(JSON.parse(JSON.stringify(defaults)));
  };

  const restoreFromHistory = async (snapshot, when) => {
    if (!snapshot) return;
    const pretty = when ? _fmtDate(when) : "this snapshot";
    if (!window.confirm(`Roll the live rate card back to ${pretty}? This will save immediately and write a new audit entry.`)) return;
    setSaving(true);
    try {
      await api.put("/bedding/rates", { rates: snapshot });
      toast.success(`Rates restored to ${pretty}`);
      // Mirror the saved snapshot into local state so the editor shows
      // the restored values without a round-trip.
      setRates(JSON.parse(JSON.stringify(snapshot)));
      loadHistory();
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err, "Restore failed"));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/bedding/rates", { rates });
      toast.success("Rate card updated");
      onOpenChange?.(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const RateInput = ({ testid, value, onChange }) => (
    <input
      data-testid={testid}
      type="number"
      step="0.001"
      min="0"
      value={Number.isFinite(value) ? value : ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-24 h-9 text-right rounded-md border border-slate-200 bg-white px-2 text-sm tabular-nums"
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bedding-rates-editor">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-teal-700" />
            Johnsons rate card
          </DialogTitle>
          <DialogDescription>
            Owner-only. Every bedding calculation across the portfolio uses these GBP (inc VAT) rates per turnover.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-center py-10 text-slate-400 italic">Loading rates…</div>
        )}

        {!loading && rates && (
          <div className="space-y-5">
            <div>
              <div className="ns-label mb-2">Duvet cover</div>
              <div className="grid grid-cols-4 gap-3">
                {BED_SIZES.map(({ key, label }) => (
                  <div key={`duvet-${key}`} className="space-y-1">
                    <div className="text-[11px] text-slate-500">{label}</div>
                    <RateInput
                      testid={`bedding-rate-duvet_cover-${key}`}
                      value={rates?.duvet_cover?.[key]}
                      onChange={(v) => setDuvetSheet("duvet_cover", key, v)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="ns-label mb-2">Sheet</div>
              <div className="grid grid-cols-4 gap-3">
                {BED_SIZES.map(({ key, label }) => (
                  <div key={`sheet-${key}`} className="space-y-1">
                    <div className="text-[11px] text-slate-500">{label}</div>
                    <RateInput
                      testid={`bedding-rate-sheet-${key}`}
                      value={rates?.sheet?.[key]}
                      onChange={(v) => setDuvetSheet("sheet", key, v)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="ns-label mb-2">Towels & other</div>
              <div className="grid grid-cols-3 gap-3">
                {SIMPLE_FIELDS.map(({ key, label }) => (
                  <div key={`simple-${key}`} className="space-y-1">
                    <div className="text-[11px] text-slate-500">{label}</div>
                    <RateInput
                      testid={`bedding-rate-${key}`}
                      value={rates?.[key]}
                      onChange={(v) => setSimple(key, v)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-100 bg-stone-50 p-3 text-[11px] text-slate-500 flex items-center gap-2">
              <PoundSterling className="w-3 h-3 text-slate-400" />
              Changes feed the next bedding matrix load. Older bulk calculations are not re-stamped.
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setHistoryOpen((v) => !v); if (!historyOpen && !history.length) loadHistory(); }}
                data-testid="bedding-rates-history-toggle"
                className="rounded-full h-9 gap-2"
              >
                <History className="w-3 h-3" />
                {historyOpen ? "Hide history" : "Rate history"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={resetDefaults}
                data-testid="bedding-rates-reset"
                className="rounded-full h-9 gap-2"
              >
                <RotateCcw className="w-3 h-3" />
                Reset to default
              </Button>
              <div className="flex-1" />
              <Button
                type="button"
                onClick={() => onOpenChange?.(false)}
                variant="outline"
                className="rounded-full h-9"
                data-testid="bedding-rates-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="bedding-rates-save"
                className="rounded-full h-9 gap-2 bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Save className="w-3 h-3" />
                {saving ? "Saving…" : "Save rate card"}
              </Button>
            </div>

            {historyOpen && (
              <div className="rounded-xl border border-slate-100 bg-white p-3 max-h-56 overflow-y-auto" data-testid="bedding-rates-history-list">
                {history.length === 0 && (
                  <div className="text-xs text-slate-400 italic text-center py-6">
                    No edits recorded yet — this will be the first.
                  </div>
                )}
                {history.map((h, i) => (
                  <div
                    key={i}
                    data-testid={`bedding-rates-history-row-${i}`}
                    className="flex items-center justify-between gap-3 border-b border-slate-100 last:border-0 py-2 text-[12px]"
                  >
                    <div className="text-slate-700 truncate min-w-0">
                      <span className="font-medium">{h.actor_name || "unknown"}</span>
                      <span className="text-slate-400"> · {_fmtDate(h.at)}</span>
                    </div>
                    <div className="text-slate-500 tabular-nums shrink-0">
                      pc {_fmtGBP(h.to_rates?.pillowcase)} · dbl duvet {_fmtGBP(h.to_rates?.duvet_cover?.double)}
                    </div>
                    <button
                      type="button"
                      data-testid={`bedding-rates-history-restore-${i}`}
                      onClick={() => restoreFromHistory(h.to_rates, h.at)}
                      disabled={saving}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white hover:border-teal-600 hover:text-teal-700 h-7 px-2 text-[11px] shrink-0 disabled:opacity-50"
                      title={`Restore the card to the state saved on ${_fmtDate(h.at)}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
